import React, { useEffect, useState } from "react";
import { popsApi, fmtUSD } from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { TrendingUp, AlertTriangle, ArrowDownWideNarrow } from "lucide-react";

const SEV = { high: "bg-[#fee2e2] text-[#b91c1c]", medium: "bg-[#fef3c7] text-[#92400e]", low: "bg-[#e0f2fe] text-[#0369a1]" };

export default function MarginTab({ period }) {
  const [groupBy, setGroupBy] = useState("customer");
  const [profit, setProfit] = useState(null);
  const [wf, setWf] = useState(null);
  const [leak, setLeak] = useState(null);

  useEffect(() => {
    popsApi.profit(period, groupBy).then(setProfit).catch(console.error);
  }, [period, groupBy]);
  useEffect(() => {
    popsApi.waterfall(period).then(setWf).catch(console.error);
    popsApi.leakage(period).then(setLeak).catch(console.error);
  }, [period]);

  const steps = wf ? [
    ["List cost", wf.steps.list_cost, "#94a3b8"],
    ["Contracted", wf.steps.contracted_cost, "#64748b"],
    ["Effective (provider)", wf.steps.effective_cost, "#0369a1"],
    ["MSP cost (L1 rated)", wf.steps.msp_cost, "#0f766e"],
    ["Customer price", wf.steps.customer_price, "#00579e"],
  ] : [];
  const maxStep = Math.max(...steps.map((s) => s[1] || 0), 1);

  return (
    <div className="space-y-4" data-testid="pops-margin-tab">
      {/* Waterfall */}
      <div className="cs-card p-5" data-testid="waterfall-panel">
        <div className="flex items-center gap-2 mb-4">
          <ArrowDownWideNarrow className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">List → Contracted → Effective → MSP cost → Customer price · {period}</h3>
          {wf && (
            <span className="ml-auto text-[11px] text-muted-foreground">
              discount captured <span className="cs-mono font-semibold text-[#15803d]">{fmtUSD(wf.discount_captured_usd)}</span>
              {" · "}L1 margin <span className="cs-mono font-semibold">{fmtUSD(wf.distributor_margin_usd)}</span>
              {" · "}MSP margin <span className="cs-mono font-semibold text-[#00579e]">{fmtUSD(wf.msp_margin_usd)}</span>
            </span>
          )}
        </div>
        <div className="space-y-2">
          {steps.map(([label, value, color]) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-44 text-xs text-muted-foreground text-right shrink-0">{label}</div>
              <div className="flex-1 bg-[hsl(var(--cs-tint))] rounded-sm h-6 relative">
                <div className="h-6 rounded-sm transition-all duration-500" style={{ width: `${(value / maxStep) * 100}%`, backgroundColor: color }} />
                <span className="absolute right-2 top-0.5 cs-mono text-[11px] font-semibold">{fmtUSD(value)}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Profit analysis */}
      <div className="cs-card p-5" data-testid="profit-panel">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-4 h-4 text-[#00579e]" />
          <h3 className="cs-heading text-sm font-semibold">Profit Analysis</h3>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="rounded-sm h-8 text-xs w-[170px] ml-auto" data-testid="profit-groupby-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="customer">by customer</SelectItem>
              <SelectItem value="service">by service</SelectItem>
              <SelectItem value="service_category">by service category</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-[hsl(var(--cs-tint))] border-b border-border">
            <tr className="text-left">
              {[groupBy, "Revenue (customer price)", "MSP cost", "Margin $", "Margin %"].map((h) => (
                <th key={h} className="px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(profit?.rows || []).map((r, i) => (
              <tr key={i} className="border-b border-border last:border-0">
                <td className="px-3 py-2 font-medium">{r[groupBy]}</td>
                <td className="px-3 py-2 text-right cs-mono">{fmtUSD(r.customer_price)}</td>
                <td className="px-3 py-2 text-right cs-mono text-muted-foreground">{fmtUSD(r.msp_cost)}</td>
                <td className={`px-3 py-2 text-right cs-mono font-semibold ${r.margin_usd < 0 ? "text-[#b91c1c]" : "text-[#15803d]"}`}>{fmtUSD(r.margin_usd)}</td>
                <td className={`px-3 py-2 text-right cs-mono ${r.margin_pct < 5 ? "text-[#b91c1c] font-semibold" : ""}`}>{r.margin_pct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Leakage */}
      <div className="cs-card p-5" data-testid="leakage-panel">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-[#b91c1c]" />
          <h3 className="cs-heading text-sm font-semibold">Revenue Leakage Digest</h3>
          {leak && (
            <span className="ml-auto text-xs">
              {leak.item_count} findings · <span className="cs-mono font-semibold text-[#b91c1c]">{fmtUSD(leak.total_impact_usd)}/mo</span> at risk
            </span>
          )}
        </div>
        <div className="space-y-2">
          {(leak?.items || []).map((it, i) => (
            <div key={i} className="flex items-start gap-3 border border-border rounded-sm p-3" data-testid={`leakage-item-${it.type}-${i}`}>
              <span className={`px-1.5 py-0.5 rounded-sm text-[9px] uppercase font-semibold shrink-0 ${SEV[it.severity]}`}>{it.severity}</span>
              <div className="min-w-0">
                <div className="text-xs font-medium">{it.title}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">→ {it.action}</div>
              </div>
              <div className="ml-auto cs-mono text-sm font-semibold text-[#b91c1c] shrink-0">{fmtUSD(it.impact_usd)}/mo</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
